import {FC, ReactNode} from 'react'

import {Button, ButtonStatus} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {FormErrorMessage} from '@/components/common/form/FormErrorMessage'
import {TramaLogo} from '@/components/common/icons/TramaLogo'
import {MODAL_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type AuthModalButtonType = {
  text?: string
  variant?: ButtonVariant
  onClick?: () => void
  isSubmitting?: boolean
  status?: ButtonStatus
  dataTest?: string
  icon?: ReactNode
}

type AuthModalLayoutProps = {
  title: string
  description?: string
  primaryButton?: AuthModalButtonType
  secondaryButton?: AuthModalButtonType
  dividerText?: string
  onCancel: () => void
  errorMessage?: string
  footer?: ReactNode
}

export const AuthModalLayout: FC<React.PropsWithChildren<AuthModalLayoutProps>> = ({
  title,
  description,
  primaryButton,
  secondaryButton,
  dividerText,
  errorMessage,
  footer,
  children,
}) => {
  return (
    <>
      <div className="wrapper">
        <div className="header">
          <div className="logo">
            <TramaLogo />
          </div>
          <Text variant={TextVariant.h4}>{title}</Text>
          {description && (
            <div className="description">
              <Text color={theme.palette.text.light}>{description}</Text>
            </div>
          )}
        </div>
        {children}
        {errorMessage && <FormErrorMessage message={errorMessage} />}
        {(primaryButton || secondaryButton) && (
          <div className="buttons">
            {primaryButton && (
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  primaryButton.onClick && primaryButton.onClick()
                }}
                variant={ButtonVariant.primary}
                fullWidth
                disabled={primaryButton.isSubmitting}
                status={primaryButton.status}
                data-test={primaryButton.dataTest}
              >
                {primaryButton.text}
              </Button>
            )}
            {primaryButton && secondaryButton && dividerText && (
              <div className="divider">
                <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                  {dividerText}
                </Text>
              </div>
            )}
            {secondaryButton && (
              <Button
                onClick={secondaryButton.onClick}
                variant={ButtonVariant.tertiary}
                fullWidth
                type="button"
                fontWeight={TextFontWeight.semibold}
                icon={secondaryButton.icon}
                iconPosition="left"
              >
                {secondaryButton.text}
              </Button>
            )}
          </div>
        )}
        {footer && <div className="footer">{footer}</div>}
      </div>

      <style jsx>{`
        .wrapper {
          min-width: ${MODAL_WIDTH};
          max-width: ${MODAL_WIDTH};
        }
        .header {
          text-align: center;
          margin-bottom: ${theme.spacing[4]};
        }
        .logo {
          display: inline-block;
          margin-bottom: ${theme.spacing[3.5]};
        }
        .description {
          margin-top: ${theme.spacing[2]};
        }
        .buttons {
          margin-top: ${theme.spacing[5]};
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing[2]};
        }
        .divider {
          display: flex;
          align-items: center;
          gap: ${theme.spacing[3]};
          margin: ${theme.spacing[3]} 0;
        }
        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background-color: ${theme.palette.gray[200]};
        }
        .footer {
          text-align: center;
          margin-top: ${theme.spacing[5]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            min-width: inherit;
            max-width: inherit;
          }
        }
      `}</style>
    </>
  )
}
